/**
 * Movepro CRM API interface
 *
 * @package Movepro CRM
 * @module Migration
 */

const base = (tenantId) => `/tenants/${tenantId}/migration`;

const sourceBase = '/migration/source';

async function getSources() {
    const { data } = await window.centralAxios.get(`${sourceBase}/sources`);
    return data;
}

async function createMigration(payload) {
    const { data } = await window.centralAxios.post(sourceBase, payload);
    return data;
}

async function submitMigration(workbookId, payload) {
    const { data } = await window.centralAxios.post(`${sourceBase}/${workbookId}/submit`, payload);
    return data;
}

async function retryMigration(workbookId) {
    const { data } = await window.centralAxios.post(`${sourceBase}/${workbookId}/retry`);
    return data;
}

async function getAllWorkbooks(params = {}) {
    const { data } = await window.centralAxios.get(`${sourceBase}/workbooks`, { params });
    return data;
}

async function getCompanies(params = {}) {
    const { data } = await window.centralAxios.get(`${sourceBase}/companies`, { params });
    return data;
}

async function getMigration(workbookId) {
    const { data } = await window.centralAxios.get(`${sourceBase}/workbooks/${workbookId}`);
    return data;
}

async function getWorkbookFiles(workbookId) {
    const { data } = await window.centralAxios.get(`${sourceBase}/workbooks/${workbookId}/files`);
    return data;
}

async function searchWorkbook(workbookId, q) {
    const { data } = await window.centralAxios.get(`${sourceBase}/workbooks/${workbookId}/search`, { params: { q } });
    return data;
}

async function getTenants() {
    const { data } = await window.centralAxios.get('/tenants-select', { params: { type: 'ALL' } });
    return data;
}

async function getWorkbooks(tenantId) {
    const { data } = await window.centralAxios.get(`${base(tenantId)}/workbooks`);
    return data;
}

async function getWorkbook(tenantId, id) {
    const { data } = await window.centralAxios.get(`${base(tenantId)}/workbooks/${id}`);
    return data;
}

async function getBatch(tenantId, id) {
    const { data } = await window.centralAxios.get(`${base(tenantId)}/batches/${id}`);
    return data;
}

async function getPreview(tenantId, batchId, count = 3, filter = null, orderId = null) {
    const params = { count };
    if (filter) {
        params.filter = filter;
    }
    if (orderId) {
        params.order_id = orderId;
    }
    const { data } = await window.centralAxios.get(`${base(tenantId)}/batches/${batchId}/preview`, { params });
    return data;
}

async function getBatchCustomers(tenantId, batchId) {
    const { data } = await window.centralAxios.get(`${base(tenantId)}/batches/${batchId}/customers`);
    return data;
}

async function getWorkbookCustomers(tenantId, workbookId) {
    const { data } = await window.centralAxios.get(`${base(tenantId)}/workbooks/${workbookId}/customers`);
    return data;
}

async function getWorkbookAffiliates(tenantId, workbookId) {
    const { data } = await window.centralAxios.get(`${base(tenantId)}/workbooks/${workbookId}/affiliates`);
    return data;
}

async function getWorkbookPreview(tenantId, workbookId, count = 3, filter = null) {
    const params = { count };
    if (filter) {
        params.filter = filter;
    }
    const { data } = await window.centralAxios.get(`${base(tenantId)}/workbooks/${workbookId}/preview`, { params });
    return data;
}

async function approveWorkbook(tenantId, id) {
    const { data } = await window.centralAxios.patch(`${base(tenantId)}/workbooks/${id}/approve`);
    return data;
}

async function rollbackWorkbook(tenantId, id) {
    const { data } = await window.centralAxios.post(`${base(tenantId)}/workbooks/${id}/rollback`);
    return data;
}

async function cancelWorkbookImport(tenantId, id) {
    const { data } = await window.centralAxios.patch(`${base(tenantId)}/workbooks/${id}/cancel`);
    return data;
}

async function verifyWorkbookRollback(tenantId, id) {
    const { data } = await window.centralAxios.get(`${base(tenantId)}/workbooks/${id}/verify-rollback`);
    return data.data;
}

async function scanTenantOrphans(tenantId) {
    const { data } = await window.centralAxios.get(`${base(tenantId)}/orphans`);
    return data.data;
}

async function resetTransformation(tenantId, batchId) {
    const { data } = await window.centralAxios.post(`${base(tenantId)}/batches/${batchId}/reset-transformation`);
    return data;
}

async function declineWorkbook(tenantId, id, reason) {
    const { data } = await window.centralAxios.patch(`${base(tenantId)}/workbooks/${id}/decline`, {
        decline_reason: reason,
    });
    return data;
}

async function reviveWorkbook(tenantId, id) {
    const { data } = await window.centralAxios.patch(`${base(tenantId)}/workbooks/${id}/revive`);
    return data;
}

async function lockMigration(tenantId) {
    const { data } = await window.centralAxios.patch(`${base(tenantId)}/lock`);
    return data;
}

async function unlockMigration(tenantId) {
    const { data } = await window.centralAxios.patch(`${base(tenantId)}/unlock`);
    return data;
}

async function deleteWorkbook(tenantId, id) {
    const { data } = await window.centralAxios.delete(`${base(tenantId)}/workbooks/${id}`);
    return data;
}

async function consolidateWorkbook(tenantId, id) {
    const { data } = await window.centralAxios.post(`${base(tenantId)}/workbooks/${id}/consolidate`);
    return data;
}

async function deleteAllWorkbooks(tenantId) {
    const { data } = await window.centralAxios.delete(`${base(tenantId)}/workbooks`);
    return data;
}

async function getWorkbookEntries(tenantId, workbookId, { status = null, page = 1, perPage = 25 } = {}) {
    const params = { page, per_page: perPage };
    if (status) {
        params.status = status;
    }
    const { data } = await window.centralAxios.get(`${base(tenantId)}/workbooks/${workbookId}/entries`, {
        params,
    });
    return data;
}

async function updateEntry(tenantId, entryId, entryData) {
    const { data } = await window.centralAxios.patch(`${base(tenantId)}/entries/${entryId}`, {
        data: entryData,
    });
    return data;
}

async function skipEntry(tenantId, entryId) {
    const { data } = await window.centralAxios.post(`${base(tenantId)}/entries/${entryId}/skip`);
    return data;
}

async function unskipEntry(tenantId, entryId) {
    const { data } = await window.centralAxios.post(`${base(tenantId)}/entries/${entryId}/unskip`);
    return data;
}

async function importEntry(tenantId, entryId) {
    const { data } = await window.centralAxios.post(`${base(tenantId)}/entries/${entryId}/import`);
    return data;
}

async function rollbackEntry(tenantId, entryId) {
    const { data } = await window.centralAxios.post(`${base(tenantId)}/entries/${entryId}/rollback`);
    return data;
}

export default {
    getSources,
    createMigration,
    submitMigration,
    retryMigration,
    getAllWorkbooks,
    getCompanies,
    getMigration,
    getWorkbookFiles,
    searchWorkbook,
    getTenants,
    getWorkbooks,
    getWorkbook,
    getBatch,
    getPreview,
    getBatchCustomers,
    getWorkbookCustomers,
    getWorkbookAffiliates,
    getWorkbookPreview,
    resetTransformation,
    approveWorkbook,
    rollbackWorkbook,
    cancelWorkbookImport,
    verifyWorkbookRollback,
    scanTenantOrphans,
    declineWorkbook,
    reviveWorkbook,
    lockMigration,
    unlockMigration,
    deleteWorkbook,
    consolidateWorkbook,
    deleteAllWorkbooks,
    getWorkbookEntries,
    updateEntry,
    skipEntry,
    unskipEntry,
    importEntry,
    rollbackEntry,
};
